import isEqual from 'react-fast-compare';
import { filter, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { replace } from '@lagunovsky/redux-react-router';
import { IEpic, isTypeOfAndGetPayload } from '@cbridge-utils/redux-observable';
import { IGetSelectorStateType } from '@cbridge-utils/reselect';
import { RouterAction, locationSelector } from '@cbridge-packages/routing';
import { defineRouteAction, RouteActionVariant } from '../_helpers/defineRouteAction';
import { DetailsSidebarAction } from '../detailsSidebar.action';
import { detailsSidebarStoreSelector } from '../detailsSidebar.selector';
import { SidebarOpenMode } from '../detailsSidebar.interface';

type IState =
  & IGetSelectorStateType<typeof detailsSidebarStoreSelector>
  & IGetSelectorStateType<typeof locationSelector>;

export const openEpic: IEpic<IState> = (action$, state$) => action$.pipe(
  isTypeOfAndGetPayload(DetailsSidebarAction.open),
  withLatestFrom(state$),
  map(([payload, state]) => {
    return {
      prevState: detailsSidebarStoreSelector(state),
      nextState: payload,
      location: locationSelector(state),
    };
  }),
  filter(({ prevState, nextState }) => !isEqual(prevState, nextState)),
  mergeMap(({ prevState, nextState, location }) => {
    const { action, locationState, segment } = defineRouteAction({ prevState, nextState });

    switch (action) {
      case RouteActionVariant.AddId:
        // "View" or "Edit" when there was no sidebar or after "Create"
        return [RouterAction.addLastPathParam({ segment, locationState })];
      case RouteActionVariant.UpdateId:
        // Another "View" or "Edit" after existing "View" or "Edit"
        return [RouterAction.updateLastPathParam({ segment, locationState })];
      case RouteActionVariant.RemoveId:
        // "Create" after "View" or "Edit"
        return [RouterAction.removeLastPathParam({ locationState })];
      default: {
        // Same id (e.g. switching to "Edit" via the row action pencil on an already open sidebar).
        // Keep the mode in location.state so it stays the single source of truth and a later
        // RouterAction.replace can reset back to "View" by clearing it.
        const isSameIdModeSwitch =
          prevState.mode !== nextState.mode
          && (nextState.mode === SidebarOpenMode.View || nextState.mode === SidebarOpenMode.Edit);

        if (isSameIdModeSwitch) {
          return [
            DetailsSidebarAction.setState(nextState),
            replace(location, { mode: nextState.mode, type: nextState.type }),
          ];
        }

        // "Create" when there was no sidebar or repeated action with the same id
        return [DetailsSidebarAction.setState(nextState)];
      }
    }
  }),
);
